import React, { useState, useEffect, useRef } from "react";
import { FaChevronDown } from "react-icons/fa";

interface SortByDropdownProps {
  label?: string;
  onSelect: (value: any) => void;
}

const sortOptions = [
  { label: 'Price: Low to High', value: 'price_low_high' },
  { label: 'Price: High to Low', value: 'price_high_low' },
  { label: 'Ratings: Low to High', value: 'ratings_low_high' },
  { label: 'Ratings: High to Low', value: 'ratings_high_low' },
];

const SortByDropdown: React.FC<SortByDropdownProps> = ({
  label = "Sort By",
  onSelect,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<'left' | 'right'>('left');

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const screenWidth = window.innerWidth;
      const spaceOnRight = screenWidth - buttonRect.right;
      const spaceOnLeft = buttonRect.left;

      if (spaceOnRight < 200 && spaceOnLeft >= 200) {
        setDropdownPosition('left');
      } else {
        setDropdownPosition('right');
      }
    }
  }, [isOpen]);

  const toggleDropdown = () => setIsOpen((prev) => !prev);

  const handleSelect = (value: any) => {
    onSelect(value);
    setIsOpen(false);
  };

  // Cast FaChevronDown to include a 'size' prop in its props
  const ChevronDown = FaChevronDown as unknown as React.FC<
    { size: number } & React.SVGProps<SVGSVGElement>
  >;

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <button
        type="button"
        onClick={toggleDropdown}
        className="inline-flex items-center space-x-1.5 rounded-md border border-[#E2E8F0] bg-white px-4 py-1.5 text-sm font-medium text-[#0F172A] hover:border-pink-500 focus:outline-none focus:ring-2 focus:ring-pink-200 transition"
        ref={buttonRef}
      >
        <span>{label}</span>
        <ChevronDown
          size={12}
          className={`transform transition-transform duration-150 ${
            isOpen ? "rotate-180" : ""
          } text-[#0F172A]`}
        />
      </button>
      {isOpen && (
        <div
          className={`absolute mt-2 min-w-[200px] bg-white rounded-xl shadow-lg border border-gray-200 z-10 p-4 ${
            dropdownPosition === 'left' ? 'right-0' : 'left-0'
          }`}
        >
          <ul>
            {sortOptions.map((item) => (
              <li
                className="cursor-pointer hover:bg-gray-100 p-2"
                onClick={() => handleSelect(item.value)}
              >
                {item.label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SortByDropdown;